import React, { useLayoutEffect, useRef } from 'react';
import { classnames } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/pro-light-svg-icons';

import theme from './RoomOptionTwoTierList.module.scss';
import { ConfiguratorGroupDto } from '@/services/configurator';
import { useOptionConfigurator } from '../../../OptionConfiguratorContext';
import DateDisplay from '@/components/localization/DateDisplay';
import { GroupRow } from './GroupRow';
import { RoomStyleSection } from '../../RoomStyle/RoomStyleSection';

interface GroupListProps {
    groups: ConfiguratorGroupDto[];
    is3d: boolean;
    activeGroupId?: number | null;
    onOpenGroup: (group: ConfiguratorGroupDto) => void;
    /** Persists the list scroll position across unmount (Tier 2) so it's restored on return. */
    scrollTopRef?: React.MutableRefObject<number>;
    /** Capture-phase guard that swallows the ghost click following a detail close (see useActiveGroup). */
    onClickCapture?: (e: React.MouseEvent) => void;
}

export const GroupList: React.FC<GroupListProps> = ({
    groups,
    is3d,
    activeGroupId,
    onOpenGroup,
    scrollTopRef,
    onClickCapture,
}) => {
    const { projectData } = useOptionConfigurator();
    const listScrollRef = useRef<HTMLDivElement>(null);

    // Restore the saved scroll position on mount (returning from an option group's items); stash the
    // latest position on unmount (opening a group). Layout effect so it's applied before paint.
    useLayoutEffect(() => {
        const el = listScrollRef.current;
        if (!el || !scrollTopRef) return;
        el.scrollTop = scrollTopRef.current;
        return () => {
            scrollTopRef.current = el.scrollTop;
        };
    }, [scrollTopRef]);
    const deadlineById = new Map((projectData?.deadlines ?? []).map((d) => [d.id, d]));

    // Organise the menu by deadline: one section per deadline present in this room, each headed by a
    // sticky separator showing the deadline name (left) and its date (right). Sections are ordered
    // chronologically by deadline date.
    const byDeadline = new Map<number, ConfiguratorGroupDto[]>();
    for (const group of groups) {
        const list = byDeadline.get(group.deadlineId);
        if (list) list.push(group);
        else byDeadline.set(group.deadlineId, [group]);
    }
    const orderedIds = [...byDeadline.keys()].sort((a, b) => {
        const da = deadlineById.get(a)?.deadline;
        const db = deadlineById.get(b)?.deadline;
        return new Date(da ?? 0).getTime() - new Date(db ?? 0).getTime();
    });

    const renderRow = (group: ConfiguratorGroupDto) => (
        <li key={`${group.deadlineId}-${group.id}`}>
            <GroupRow
                group={group}
                is3d={is3d}
                active={group.id === activeGroupId}
                onClick={() => onOpenGroup(group)}
            />
        </li>
    );

    return (
        <div className={classnames(theme.twoTier, theme.detail)} onClickCapture={onClickCapture}>
            {/* Only the list scrolls; each deadline's separator sticks to the top while its section
                is in view (replaces the old fixed room-name header). */}
            <div className={theme.listScroll} ref={listScrollRef}>
                <ul className={theme.groupList}>
                    <RoomStyleSection />
                    {orderedIds.map((id) => {
                        const meta = deadlineById.get(id);
                        return (
                            <React.Fragment key={id}>
                                <li className={theme.deadlineSeparator}>
                                    <span className={theme.separatorLabel}>
                                        <FontAwesomeIcon className={theme.separatorIcon} icon={faCalendar} />
                                        <span className={theme.deadlineName}>
                                            {meta?.name ?? <Trans>Other options</Trans>}
                                        </span>
                                    </span>
                                    {meta?.deadline && (
                                        <span className={theme.deadlineDate}>
                                            <DateDisplay value={meta.deadline} monthTextForm />
                                        </span>
                                    )}
                                </li>
                                {byDeadline.get(id)?.map(renderRow)}
                            </React.Fragment>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
};
